/**
 * Which step of an artwork upload failed, so a failure can be told to the member and
 * counted (`image_upload_failed`, #4159). Framework-free: the client and town-square both
 * wrap their own upload steps with `atStep` and read the step back with `stepOf`.
 *
 * - pick        the file itself: refused by the picker, unreadable, or not convertible
 * - signed_url  asking the API where to send it
 * - s3_put      sending the bytes
 * - create      saving the image row
 * - processing  the image processor gave up on it
 */
export const UPLOAD_FAILURE_STEPS = ['pick', 'signed_url', 's3_put', 'create', 'processing'] as const;

export type UploadFailureStep = (typeof UPLOAD_FAILURE_STEPS)[number];

export type UploadRejection = 'type' | 'size' | 'count' | 'other';

type Tagged = Error & { uploadStep?: UploadFailureStep };

function toError(error: unknown): Tagged {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === 'string' ? error : 'Upload failed');
}

/** Runs one upload step; a throw comes back tagged with the step it happened in. */
export async function atStep<T>(step: UploadFailureStep, run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    const tagged = toError(error);
    // The innermost step wins: a nested atStep already said where it broke.
    tagged.uploadStep = tagged.uploadStep ?? step;
    throw tagged;
  }
}

/** The step a failure was tagged with, or `fallback` for an error no step claimed. */
export function stepOf(error: unknown, fallback: UploadFailureStep = 'create'): UploadFailureStep {
  return (error as Tagged | null)?.uploadStep ?? fallback;
}

/** The `image_upload_failed` payload. Message is clipped so a stack never lands in analytics. */
export function failureProperties(
  step: UploadFailureStep,
  file: { type?: string; size?: number } | null | undefined,
  error?: unknown
) {
  const message = error === undefined ? undefined : toError(error).message.slice(0, 200);

  return {
    step,
    file_type: file?.type || 'unknown',
    file_size: file?.size ?? 0,
    ...(message ? { error_message: message } : {}),
  };
}

const REJECTIONS: Record<string, UploadRejection> = {
  'file-invalid-type': 'type',
  'file-too-large': 'size',
  'too-many-files': 'count',
};

/** Why a file picker refused a file, from react-dropzone's error codes. */
export function rejectionReason(codes: string[]): UploadRejection {
  const known = codes.map((code) => REJECTIONS[code]).find(Boolean);
  return known ?? 'other';
}
